/**
 * Action inbox — the shared model behind the Overview card and /action-inbox.
 *
 * One builder, two surfaces, so the card and the page can never disagree about
 * how many things are outstanding.
 *
 * Two corrections over the first version, which lived inline in OverviewPage:
 *
 * 1. EVERY task pointed at `/regjistrimi`. The guided flow is a fine place to
 *    start a month, but it is not where you go to close one specific gap, so an
 *    item that said "Warehouse has no data in March" still left the user to find
 *    March and Warehouse themselves. Tasks now carry `site` and `period` in the
 *    query string and land on the screen that closes that exact gap.
 *
 * 2. It counted all 12 months of the year as gaps. In July that reports August
 *    through December as "missing data" — five phantom gaps per facility, which
 *    makes the inbox both alarming and wrong. Future months are not gaps.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `?site=10&period=2026-03` — read by the ported entry screens to prefill step 1. */
const deepLink = (to, siteId, year, month) =>
  `${to}?site=${siteId}&period=${year}-${String(month).padStart(2, '0')}`;

/**
 * Months that can legitimately hold data: everything up to and including the
 * current month for the current year, all 12 for a past year.
 */
export function elapsedMonths(year) {
  const now = new Date();
  if (year < now.getFullYear()) return 12;
  if (year > now.getFullYear()) return 0;
  return now.getMonth() + 1;
}

const SEVERITY = { e: 0, w: 1, i: 2, o: 3 };

/**
 * @returns {{ tasks: Array, counts: {open:number,gaps:number,partial:number} }}
 * `tasks` is every actionable item, most severe first. The Overview card shows
 * the head of this list; the page shows all of it.
 */
export function buildActionInbox({ kpis, matrix }) {
  const tasks = [];
  const year = matrix?.year || new Date().getFullYear();
  const elapsed = elapsedMonths(year);

  let gaps = 0;
  let partial = 0;

  if (matrix?.sites?.length) {
    matrix.sites.forEach((s) => {
      s.months
        .filter((m) => m.month <= elapsed)
        .forEach((m) => {
          if (m.state === 'none') {
            gaps += 1;
            tasks.push({
              id: `gap-${s.id}-${m.month}`,
              groupKey: 'inbox.group.missing',
              tone: 'w', icon: 'Warn',
              titleKey: 'inbox.task.noData',
              titleVars: { site: s.name, month: MONTHS[m.month - 1], year },
              bodyKey: 'inbox.task.noDataBody',
              actionKey: 'inbox.task.addData',
              to: deepLink('/scope-1/djegja-stacionare', s.id, year, m.month)
            });
          } else if (m.state === 'partial') {
            partial += 1;
            // hasS1/hasS2 tell us which half is missing, so the task can name it
            // and link to the screen that closes it.
            const missingS2 = m.hasS1 && !m.hasS2;
            const missingS1 = !m.hasS1 && m.hasS2;
            const scope = missingS2 ? 'Scope 2' : missingS1 ? 'Scope 1' : null;
            const to = missingS2
              ? deepLink('/scope-2/electricity', s.id, year, m.month)
              : deepLink('/scope-1/djegja-stacionare', s.id, year, m.month);
            tasks.push({
              id: `partial-${s.id}-${m.month}`,
              groupKey: 'inbox.group.partial',
              tone: 'i', icon: 'Info',
              titleKey: 'inbox.task.missingScope',
              titleVars: { site: s.name, month: MONTHS[m.month - 1], year, scope },
              bodyKey: missingS2 ? 'inbox.task.missingS2Body'
                : missingS1 ? 'inbox.task.missingS1Body'
                  : 'inbox.task.oneScopeBody',
              actionKey: 'inbox.task.complete',
              to
            });
          }
        });
    });
  }

  if (matrix?.unassignedRecords > 0) {
    tasks.push({
      id: 'unassigned',
      groupKey: 'inbox.group.attribution',
      tone: 'w', icon: 'Warn',
      titleKey: 'inbox.task.unassigned',
      titleVars: { count: matrix.unassignedRecords },
      bodyKey: 'inbox.task.unassignedBody',
      badge: String(matrix.unassignedRecords),
      actionKey: 'inbox.task.reviewRecords',
      to: '/records'
    });
  }

  const vb = kpis?.vsBaseline;
  if (vb && vb.vsBaselinePct !== null && vb.onTrack === false) {
    tasks.push({
      id: 'off-track',
      groupKey: 'inbox.group.trajectory',
      tone: 'e', icon: 'Alert',
      titleKey: 'inbox.task.offTrack',
      titleVars: { year: vb.targetYear },
      bodyKey: 'inbox.task.offTrackBody',
      bodyVars: {
        pct: Number(vb.vsBaselinePct).toFixed(1),
        baseline: vb.baselineYear,
        required: Number(vb.requiredReductionPct).toFixed(1)
      },
      actionKey: 'inbox.task.openExplorer',
      to: '/explorer'
    });
  }

  const bio = kpis?.biogenicMemo;
  if (bio && bio.value > 0) {
    tasks.push({
      id: 'biogenic',
      groupKey: 'inbox.group.info',
      tone: 'o', icon: 'Check',
      titleKey: 'inbox.task.biogenic',
      titleVars: { value: Number(bio.value).toFixed(3) },
      bodyKey: 'inbox.task.biogenicBody',
      actionKey: 'inbox.task.openExplorer',
      to: '/explorer',
      informational: true
    });
  }

  tasks.sort((a, b) => (SEVERITY[a.tone] ?? 9) - (SEVERITY[b.tone] ?? 9));

  return {
    tasks,
    counts: {
      open: tasks.filter((t) => !t.informational).length,
      gaps,
      partial
    }
  };
}

export const GROUP_ORDER = ['inbox.group.trajectory', 'inbox.group.missing', 'inbox.group.partial', 'inbox.group.attribution', 'inbox.group.info'];
